import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Drawer,
  Group,
  NumberInput,
  Select,
  Stack,
  Switch,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { isAxiosError } from 'axios';
import type { RestaurantTableDto, RestaurantZoneDto } from './types';
import { useCreateTable, useUpdateTable } from './floorPlanApi';

interface Props {
  opened: boolean;
  onClose: () => void;
  branchId: string;
  defaultZoneId: string | null;
  zones: RestaurantZoneDto[];
  table: RestaurantTableDto | null;
}

interface FormValues {
  zoneId: string | null;
  code: string;
  nameAr: string;
  nameEn: string;
  capacity: number;
  sortOrder: number;
  isActive: boolean;
}

export function TableFormDrawer({
  opened,
  onClose,
  branchId,
  defaultZoneId,
  zones,
  table,
}: Props) {
  const { t, i18n } = useTranslation();
  const create = useCreateTable();
  const update = useUpdateTable();
  const submitting = create.isPending || update.isPending;
  const editing = table !== null;
  const drawerPosition = i18n.language === 'ar' ? 'left' : 'right';

  const form = useForm<FormValues>({
    initialValues: {
      zoneId: null,
      code: '',
      nameAr: '',
      nameEn: '',
      capacity: 2,
      sortOrder: 0,
      isActive: true,
    },
    validate: {
      zoneId: (v) => (v ? null : t('common.errors.required')),
      code: (v) => (v.trim() ? null : t('common.errors.required')),
      nameAr: (v) => (v.trim() ? null : t('common.errors.required')),
      capacity: (v) => (v >= 1 ? null : t('floorPlan.tableForm.capacityMin')),
    },
  });

  useEffect(() => {
    if (!opened) return;
    form.setValues({
      zoneId: table?.restaurantZoneId ?? defaultZoneId,
      code: table?.code ?? '',
      nameAr: table?.nameAr ?? '',
      nameEn: table?.nameEn ?? '',
      capacity: table?.capacity ?? 2,
      sortOrder: table?.sortOrder ?? 0,
      isActive: table?.isActive ?? true,
    });
    form.resetDirty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, table, defaultZoneId]);

  const zoneOptions = zones.map((z) => ({
    value: z.restaurantZoneId,
    label: i18n.language === 'ar' ? z.nameAr : (z.nameEn ?? z.nameAr),
  }));

  const submit = async (values: FormValues) => {
    if (!values.zoneId) return;
    const payload = {
      restaurantZoneId: values.zoneId,
      code: values.code.trim(),
      nameAr: values.nameAr.trim(),
      nameEn: values.nameEn.trim() || null,
      capacity: values.capacity,
      sortOrder: values.sortOrder,
    };
    try {
      if (editing) {
        await update.mutateAsync({
          id: table.restaurantTableId,
          request: { ...payload, isActive: values.isActive },
        });
      } else {
        await create.mutateAsync({
          branchId,
          ...payload,
        });
      }
      notifications.show({
        color: 'green',
        title: editing ? t('common.toasts.updated') : t('common.toasts.created'),
        message: payload.nameAr,
      });
      onClose();
    } catch (err) {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: extractApiMessage(err) ?? t('common.toasts.errorBody'),
      });
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={editing ? t('floorPlan.tableForm.editTitle') : t('floorPlan.tableForm.createTitle')}
      position={drawerPosition}
      size="md"
      padding="lg"
    >
      <form onSubmit={form.onSubmit(submit)}>
        <Stack gap="md">
          <Select
            label={t('floorPlan.tableForm.zoneLabel')}
            data={zoneOptions}
            value={form.values.zoneId}
            onChange={(v) => form.setFieldValue('zoneId', v)}
            withAsterisk
            searchable
          />
          <TextInput
            label={t('floorPlan.tableForm.codeLabel')}
            description={t('floorPlan.tableForm.codeHelp')}
            placeholder="T-12"
            withAsterisk
            {...form.getInputProps('code')}
          />
          <TextInput
            label={t('floorPlan.tableForm.nameArLabel')}
            placeholder={t('floorPlan.tableForm.nameArPlaceholder')}
            withAsterisk
            {...form.getInputProps('nameAr')}
          />
          <TextInput
            label={t('floorPlan.tableForm.nameEnLabel')}
            placeholder={t('floorPlan.tableForm.nameEnPlaceholder')}
            {...form.getInputProps('nameEn')}
          />
          <Group grow>
            <NumberInput
              label={t('floorPlan.tableForm.capacityLabel')}
              min={1}
              withAsterisk
              {...form.getInputProps('capacity')}
            />
            <NumberInput
              label={t('floorPlan.tableForm.sortOrderLabel')}
              min={0}
              {...form.getInputProps('sortOrder')}
            />
          </Group>
          {editing && (
            <Switch
              label={t('floorPlan.tableForm.activeLabel')}
              description={t('floorPlan.tableForm.activeHelp')}
              checked={form.values.isActive}
              onChange={(event) => form.setFieldValue('isActive', event.currentTarget.checked)}
            />
          )}

          <Group justify="flex-end" gap="sm" mt="sm">
            <Button variant="default" onClick={onClose} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={submitting}>
              {editing ? t('common.save') : t('common.create')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}

function extractApiMessage(err: unknown): string | null {
  if (isAxiosError(err)) {
    const data = err.response?.data as { detail?: string; title?: string } | undefined;
    return data?.detail ?? data?.title ?? err.message;
  }
  return null;
}
